import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { createMonitoringEvent } from '../../services/farmMapping/monitoring/monitoringEvents/createMonitoringEvent'
import { getMonitoringEventsByFarm } from '../../services/farmMapping/monitoring/monitoringEvents/getMonitoringEvents'
import { removeMonitoringEvent } from '../../services/farmMapping/monitoring/monitoringEvents/removeMonitoringEvent'
import { addMonitoringEvent } from '../../services/ruutsApi/addMonitoringEvent'
import compareGpsData from '../../services/ruutsApi/compareGpsData'
import { queryKeys } from './queryKeys'

// Every mutation here suppresses the global error snackbar because each consumer already renders
// its own error UI (EventPanel snackbar, EventPlanning/CreateEvent confirmation-loading modal,
// GPX form-level uploadError).

export const useMonitoringEvents = (farmId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.monitoringEvents(farmId),
    queryFn: ({ signal }) => getMonitoringEventsByFarm({ farmId: farmId as string, signal }),
    enabled: !!farmId,
  })

// Tasks and activities are cached by arbitrary query objects outside the farm hierarchy, so any
// write that changes them (monitoring events, site deletion) must bust both roots by name.
export const invalidateTasksAndActivities = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.monitoringTasksRoot })
  queryClient.invalidateQueries({ queryKey: queryKeys.monitoringActivitiesRoot })
}

export const useCreateMonitoringEvent = (farmId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => createMonitoringEvent({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringEvents(farmId) })
      invalidateTasksAndActivities(queryClient)
    },
    meta: { suppressGlobalError: true },
  })
}

export const useRemoveMonitoringEvent = (farmId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: string) => removeMonitoringEvent({ eventId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringEvents(farmId) })
      invalidateTasksAndActivities(queryClient)
    },
    meta: { suppressGlobalError: true },
  })
}

export const useAddMonitoringEvent = (farmId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables: Record<string, unknown>) => addMonitoringEvent(variables),
    // Cross-domain side effects: creating a GPX-confirmed event also creates MonitoringActivity rows
    // and can confirm a MonitoringSite location, so all three domains are invalidated (never setQueryData).
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringEvents(farmId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farmId) })
      invalidateTasksAndActivities(queryClient)
    },
    meta: { suppressGlobalError: true },
  })
}

// GPX validation step: the result feeds LOCAL component state (waypoints / sites-with-track-points),
// it is not a persisted domain entity — hence no cache write of any kind.
export const useCompareGpsData = () =>
  useMutation({
    mutationFn: (variables: { farm: { id: string }; file: unknown; [key: string]: unknown }) =>
      compareGpsData(variables),
    meta: { suppressGlobalError: true },
  })
